import React, { useState, useEffect, useRef, Suspense } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation
} from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import MyArtists from './pages/MyArtists';
import Compare from './pages/Compare';
import LionAuto from './pages/LionAuto';
import NoteRequests from './pages/NoteRequests';
import ErrorBoundary from './components/ErrorBoundary';
import { getMe, logout } from './api';
import { isAdminLike, isSuperAdmin } from './utils/roles';
import { safeNextPath } from './utils/safeUrl';
import { pageTitle } from './utils/pageTitle';

// Lazy: the super-admin Reports page stays out of the main bundle; every other page is eager
const Reports = React.lazy(() => import('./pages/Reports'));

// After a successful login, land the user back where they were sent from.
// ?next= is attacker-reachable, so it goes through safeNextPath first.
function LoginRoute({ user, onLogin }) {
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'), '/');
  if (user) return <Navigate to={next} replace />;
  return <Login onLogin={onLogin} />;
}

// Send an unauthenticated visitor to login while remembering the page they
// wanted, so a deep link (e.g. a Compare URL with header params) survives.
function RedirectToLogin() {
  const { pathname, search } = useLocation();
  const next = `${pathname}${search}`;
  const to = next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`;
  return <Navigate to={to} replace />;
}

// Keeps the browser tab title in step with the URL so open tabs are distinguishable.
function TabTitle() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    document.title = pageTitle(pathname, search);
  }, [pathname, search]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    // The session is an httpOnly cookie the page can't read; the cached user
    // object only seeds state to skip a loading flash. /auth/me is the
    // authority and is ALWAYS called — including with no cached user, since
    // the cookie can outlive a cleared localStorage (another tab's failed
    // bootstrap used to wipe it for everyone).
    let cancelled = false;
    const savedUser = localStorage.getItem('user');

    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    // canRetry is true only on the first attempt, so a 429 buys exactly one
    // more call and never a loop.
    const check = (canRetry) => {
      getMe()
        .then((res) => {
          if (cancelled) return;
          const fresh = { ...res.data };
          localStorage.setItem('user', JSON.stringify(fresh));
          setUser(fresh);
        })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 401) {
            // The only definite "you are logged out". 429/5xx/network mean
            // we don't know, so keep whatever cached user we have.
            localStorage.removeItem('user');
            setUser(null);
            return;
          }
          if (status === 429 && canRetry) {
            // Tab burst against the shared per-IP budget. Wait out the window
            // the server named, then try once more.
            const raw = Number(err?.response?.data?.retryAfter);
            const secs = Math.min(60, Math.max(1, Number.isFinite(raw) ? raw : 5));
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = null;
              if (!cancelled) check(false);
            }, secs * 1000 + 250);
          }
          // Anything else: keep the cached user and let the page render.
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    check(true);

    // StrictMode double-invokes effects in dev; clear the pending retry so a
    // torn-down mount never writes state or spends another token.
    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const handleLogin = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    logout().catch(() => { /* cookie may already be gone */ });
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <BrowserRouter>
      <TabTitle />
      <Routes>
        <Route
          path="/login"
          element={<LoginRoute user={user} onLogin={handleLogin} />}
        />
        <Route
          path="/"
          element={
            user ? (
              user.role === 'buyer' ? (
                <Navigate to="/orders" />
              ) : (
                <Dashboard user={user} onLogout={handleLogout} />
              )
            ) : (
              <RedirectToLogin />
            )
          }
        />
        {/* Note requests — the only page a buyer can see; staff get the queue */}
        <Route
          path="/orders"
          element={
            user ? (
              <NoteRequests user={user} onLogout={handleLogout} />
            ) : (
              <RedirectToLogin />
            )
          }
        />
        <Route
          path="/admin"
          element={
            isAdminLike(user?.role) ? (
              <Admin user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        {/* Reports: super admin only (the API enforces it too) */}
        <Route
          path="/reports"
          element={
            isSuperAdmin(user?.role) ? (
              <ErrorBoundary>
                <Suspense fallback={<div className="loading">Loading…</div>}>
                  <Reports user={user} onLogout={handleLogout} />
                </Suspense>
              </ErrorBoundary>
            ) : user ? (
              <Navigate to="/" replace />
            ) : (
              <RedirectToLogin />
            )
          }
        />
        <Route
          path="/my-artists"
          element={
            user?.role === 'pricer' ? (
              <MyArtists user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/compare/:eventId"
          element={
            user && user.role !== 'buyer' ? (
              <Compare user={user} />
            ) : user ? (
              <Navigate to="/orders" replace />
            ) : (
              <RedirectToLogin />
            )
          }
        />
        {/* Lion Auto: Compare's automation sibling, same access as Compare */}
        <Route
          path="/lion-auto/:eventId"
          element={
            user && user.role !== 'buyer' ? (
              <LionAuto user={user} />
            ) : user ? (
              <Navigate to="/orders" replace />
            ) : (
              <RedirectToLogin />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
